import React, { useState } from 'react';
import type { ListViewProps } from './ListView.types';
import { Badge } from '../../components/Badge/Badge';
import { Navbar } from '../../components/Navbar/Navbar';
import { NavigationRailGroup } from '../../components/NavigationRailGroup/NavigationRailGroup';
import { Button } from '../../components/Button/Button';
import { Table } from '../../components/Table/Table';
import { Input } from '../../components/Input/Input';
import { Select } from '../../components/Select/Select';
import type { TableColumn } from '../../components/Table/Table.types';
import {
  HomeIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  CloudIcon,
  SquaresPlusIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ListBulletIcon,
  FunnelIcon,
  ChevronDownIcon,
  EnvelopeIcon,
} from './icons';

/**
 * ListView - Vista de lista completa del sistema de diseño Siesa
 *
 * Vista completa que compone los siguientes componentes del sistema:
 * - NavigationRailGroup: Barra lateral de navegación con panel expandible
 * - Navbar: Barra superior con logo, título, notificaciones y dropdown de usuario
 * - Badge: Filtros rápidos con badges de colores
 * - Table: Tabla de datos con sorting y paginación integrada
 * - Button: Botones de acción (Crear, Auditoría, Filtrar)
 * - Input: Buscador integrado
 * - Select: Dropdown de opciones de vista
 *
 * Mejores prácticas implementadas:
 * - REUSO DE COMPONENTES: Usa componentes del sistema, NUNCA reimplementa
 * - Orden de modificadores: {responsive}:{dark}:{state}:{utility}
 * - Dark mode con estrategia 'class' (darkMode: 'class')
 * - Tokens de color consistentes con la documentación
 * - Type safety con TypeScript estricto
 * - Accesibilidad con ARIA labels
 * - Responsive design (mobile-first)
 *
 * @see docs/colors.md - Sistema de colores
 * @see docs/typography.md - Sistema tipográfico
 * @see docs/spacing.md - Sistema de espaciado
 * @see src/components/NavigationRailGroup - Componente de navegación lateral
 * @see src/components/Navbar - Componente de barra superior
 * @see src/components/Table - Componente de tabla con paginación
 * @see src/components/Button - Componente de botón
 * @see src/components/Badge - Componente de badge
 *
 * @example
 * ```tsx
 * <ListView
 *   title="List View"
 *   tableTitle="Usuarios"
 *   columns={columns}
 *   data={users}
 *   userDropdown={{
 *     avatar: '/avatar.jpg',
 *     name: 'John Doe',
 *     role: 'Administrador',
 *   }}
 *   pagination={{
 *     currentPage: 1,
 *     totalPages: 10,
 *     onPageChange: (page) => console.log(page),
 *   }}
 * />
 * ```
 */
export const ListView = <T extends Record<string, any>>({
  title = 'List View',
  navbarProductName = 'SB Commercial',
  tableTitle = 'Usuarios',
  columns,
  data,
  navigationItems,
  quickFilters,
  actionButtons,
  userDropdown,
  notifications,
  environmentBadge,
  showNavigationRail = true,
  navigationRailState = 'collapsed',
  onNavigationRailStateChange,
  onNavItemClick,
  onSearch,
  searchPlaceholder = 'Buscar',
  onSort,
  sortColumn: externalSortColumn,
  sortDirection: externalSortDirection,
  pagination,
  loading = false,
  loadingRows = 5,
  emptyMessage = 'No hay datos disponibles',
  className = '',
  onNotificationsClick,
  viewOptions,
  selectedViewOption,
  onViewOptionChange,
}: ListViewProps<T>) => {
  // ===== ESTADO INTERNO =====
  const [searchValue, setSearchValue] = useState('');
  const [showViewDropdown, setShowViewDropdown] = useState(false);

  // ===== NAVIGATION ITEMS DEFAULT =====
  const defaultNavigationItems = navigationItems || [
    {
      id: 'home',
      icon: <HomeIcon className="w-4 h-4" />,
      label: 'Inicio',
      active: true,
    },
    {
      id: 'option1',
      icon: <GlobeAltIcon className="w-4 h-4" />,
      label: 'Opción 1',
    },
    {
      id: 'option2',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      label: 'Opción 2',
    },
    {
      id: 'option3',
      icon: <CloudIcon className="w-4 h-4" />,
      label: 'Opción 3',
    },
    {
      id: 'more',
      icon: <SquaresPlusIcon className="w-4 h-4" />,
      label: 'Ver más...',
    },
  ];

  // ===== QUICK FILTERS DEFAULT =====
  const defaultQuickFilters = quickFilters || [
    {
      id: 'paid',
      label: 'Facturas Pagadas',
      color: 'lime' as const,
    },
    {
      id: 'pending',
      label: 'Facturas Pendientes por Pago',
      color: 'rose' as const,
    },
    {
      id: 'orders',
      label: 'Órdenes de Compra',
      color: 'sky' as const,
    },
  ];

  // ===== ACTION BUTTONS DEFAULT =====
  const defaultActionButtons = actionButtons || [
    {
      id: 'audit',
      label: 'Auditoría',
      leftIcon: <ListBulletIcon className="w-4 h-4" />,
      type: 'default' as const,
      onClick: () => {},
    },
    {
      id: 'filter',
      label: 'Filtrar',
      leftIcon: <FunnelIcon className="w-4 h-4" />,
      type: 'default' as const,
      onClick: () => {},
    },
    {
      id: 'create',
      label: 'Crear',
      leftIcon: <PlusIcon className="w-4 h-4" />,
      type: 'default' as const,
      onClick: () => {},
    },
  ];

  // ===== HANDLERS =====
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    onSearch?.(e.target.value);
  };

  // ===== CONVERTIR COLUMNAS AL FORMATO DE TABLE =====
  const tableColumns: TableColumn<T>[] = columns.map((col) => ({
    header: col.header,
    accessor: col.accessor,
    sortable: col.sortable,
    render: col.render,
    align: col.align,
    width: col.width,
  }));

  return (
    <div
      className={[
        'bg-background-secondary',
        'dark:bg-dark-bg-primary',
        'flex',
        'w-full',
        'h-full',
        'min-h-screen',
        className,
      ].join(' ')}
    >
      {/* ===== NAVIGATION RAIL GROUP ===== */}
      {showNavigationRail && (
        <NavigationRailGroup
          state={navigationRailState}
          items={defaultNavigationItems}
          onStateChange={onNavigationRailStateChange}
          onItemClick={onNavItemClick}
          showSearchButton={true}
        />
      )}

      {/* ===== CONTENEDOR PRINCIPAL ===== */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* ===== NAVBAR ===== */}
        <Navbar
          productName={navbarProductName}
          environmentBadge={environmentBadge}
          userDropdown={userDropdown}
          notifications={notifications}
          hideActionButtons={false}
          onNotificationsClick={onNotificationsClick}
          className="bg-background-secondary dark:bg-dark-bg-primary"
        />

        {/* ===== QUICK FILTERS BAR ===== */}
        <div className="flex items-center gap-2 px-4 py-2 w-full bg-background-secondary dark:bg-dark-bg-primary">
          {defaultQuickFilters.map((filter) => (
            <Badge
              key={filter.id}
              color={filter.color}
              label={filter.label}
              leftIcon={filter.icon}
            />
          ))}
        </div>

        {/* ===== BODY ===== */}
        <div className="flex flex-1 overflow-hidden bg-background-secondary dark:bg-dark-bg-primary">
          <div className="flex flex-col gap-4 flex-1 bg-white dark:bg-dark-bg-primary p-4 rounded-tl-3xl overflow-hidden">
            {/* Header Row */}
            <div className="flex items-center justify-between w-full">
              <h2 className="font-bold text-xl leading-7 tracking-[-0.5px] text-content-primary dark:text-dark-content-primary">
                {title}
              </h2>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {defaultActionButtons.map((btn) => (
                  <Button
                    key={btn.id}
                    type={btn.type}
                    size="sm"
                    leftIcon={btn.leftIcon}
                    onClick={btn.onClick}
                  >
                    {btn.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* ===== TABLE CONTAINER ===== */}
            <div className="flex flex-col gap-4 flex-1 bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-xl p-8 overflow-hidden">
              {/* Table Header */}
              <div className="flex items-center gap-4 w-full">
                <h3 className="flex-1 font-bold text-base leading-6 text-content-primary dark:text-dark-content-primary overflow-hidden text-ellipsis whitespace-nowrap">
                  {tableTitle}
                </h3>
                <Input
                  placeholder={searchPlaceholder}
                  startIcon={<EnvelopeIcon className="w-4 h-4" />}
                  value={searchValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleSearch(e);
                  }}
                  className="w-[200px]"
                />
                {/* View Dropdown */}
                {viewOptions && (
                  <div className="relative">
                    <button
                      onClick={() => setShowViewDropdown(!showViewDropdown)}
                      className="flex items-center gap-3 px-3 py-2 bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-lg shadow-xs hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-colors"
                    >
                      <span className="font-bold text-sm text-content-secondary dark:text-content-secondary">
                        Ver
                      </span>
                      <ChevronDownIcon className="w-5 h-5 text-content-secondary dark:text-content-secondary" />
                    </button>
                    {showViewDropdown && (
                      <div className="absolute top-full right-0 mt-1 bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-lg shadow-lg z-10 min-w-[150px]">
                        {viewOptions.map((option) => (
                          <button
                            key={option.id}
                            onClick={() => {
                              onViewOptionChange?.(option.id);
                              setShowViewDropdown(false);
                            }}
                            className={[
                              'block',
                              'w-full',
                              'px-4',
                              'py-2',
                              'text-left',
                              'text-sm',
                              'hover:bg-background-secondary',
                              'dark:hover:bg-dark-bg-secondary',
                              selectedViewOption === option.id
                                ? 'bg-primary-100 dark:bg-primary-600/20'
                                : '',
                            ].join(' ')}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ===== TABLE ===== */}
              <Table
                columns={tableColumns}
                data={data}
                variant="grid"
                showBorder={false}
                showShadow={false}
                loading={loading}
                loadingRows={loadingRows}
                emptyMessage={emptyMessage}
                onSort={onSort}
                sortColumn={externalSortColumn}
                sortDirection={externalSortDirection}
                pagination={pagination}
                className="flex-1"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
